import React, {useContext, useEffect, useRef, useState} from 'react';
import cn from 'classnames';
import { IBaseElementProps } from 'tbk-components/src/components/BasicElement';
import { ILink } from 'tbk-components/src/components/Button';
import withAppContext from "tbk-components/src/hooks/withAppContext";
import Loading from "tbk-components/src/components/Loading";

/**
 * @useTermData
 * @taxonomy location_type
 * @forceLinkedData
 */
export interface ILocationTypeTax {
	term_id?: number;
	name?: string;
}

/**
 * @usePostData
 * @post_type location
 * @forceLinkedData
 */
export interface ILocationTileItems {
	/**
	 * Tile Item Heading
	 */
	heading?: string;
	/**
	 * Tile Item Text
	 * @control richtext
	 */
	text?: string;
	/**
	 * Tile Item Heading
	 */
	link?: ILink;
}

export interface ILocationTileProps extends IBaseElementProps {
	/**
	 * @noUI
	 */
	context,
	/**
	 * Lead in Heading
	 */
	leadInHeading?: string;
	/**
	 * Lead in Text
	 * @control richtext
	 */
	leadInText?: string;
	/**
	 *	Lead in Content Layout on Desktop
	 */
	leadInContentLayout?: 'Inline' | 'Column';
	/**
	 * Tiles Heading
	 */
	heading?: string;
	/**
	 *  Tile Style
	 */
	tileStyle?: 'outlined' | 'solid';
	/**
	 * Select one or more locations to display in the component.
	 */
	tileItems?: ILocationTileItems[];
	/**
	 * Choose a location type to filter by, or leave blank for all locations
	 */
	filterByLocationType?: ILocationTypeTax[];
}

/**
 * Location Tile
 * @block
 * @icon media-spreadsheet
 */
const LocationTile: React.FC<ILocationTileProps> = withAppContext( ({
	context,
	leadInHeading,
	leadInText,
	leadInContentLayout = 'Inline',
	heading,
	tileStyle,
	tileItems = [],
	filterByLocationType,
	id,
	className,
	classNames = [],
}) => {
	//@ts-ignore
	const {Api} = useContext(context);
	const [displayTileItems, setDisplayTileItems] = useState<ILocationTileProps['tileItems']>([]);
	const ref = useRef(null);
	const [loading, setLoading] = useState<boolean>(false);

	useEffect(() => {
		if (!ref.current) {
			return;
		}

		const leadInText = ref.current.querySelector(
			'.calian-location-tile__lead-in--text',
		);

		if (leadInText) {
			if (leadInText.innerHTML === '<p><br></p>') {
				leadInText.innerHTML = '';
			}
		}
	}, []);

	useEffect(() => {
		const processedLocations = tileItems == null ? [] : [...tileItems];

		if ( processedLocations.length === 0 ) {
			processedLocations.push(null);
		}

		setLoading(true);
		//@ts-ignore
		const locationIDs = processedLocations.map((location) => location?.ID);

		Api.populateLocations({ 'locations' : locationIDs, 'types': filterByLocationType })
			.then((results) => {
				if ( results.length === 0 ) {
					setDisplayTileItems(tileItems);
				} else {
					setDisplayTileItems(results);
				}
			}).finally(() => {
				setLoading(false);
			});
	}, [ JSON.stringify( tileItems ) ]);

	if (loading) {
		return <Loading />;
	}

	return (
		<div
			id={id}
			className={
				className ||
				cn(
					'calian-location-tile',
					tileStyle === 'solid' && 'calian-location-tile--solid',
					...classNames,
				)
			}
			ref={ref}
		>
			<div className="calian-location-tile__container">
				{leadInHeading && leadInText && (
					<div
						className={cn(
							'calian-location-tile__lead-in row',
							leadInContentLayout === 'Column' && 'column-layout',
						)}
					>
						<div className="col-12 col-xl-5">
							<h2 className="calian-location-tile__lead-in--heading">
								{leadInHeading}
							</h2>
						</div>
						<div className="col-12 col-xl-7">
							<div className="calian-location-tile__lead-in--text">
								<p
									dangerouslySetInnerHTML={{
										__html: leadInText,
									}}
								/>
							</div>
						</div>
					</div>
				)}
				{heading && (
					<h3 className="calian-location-tile__heading">{heading}</h3>
				)}
				{displayTileItems?.length > 0 && (
					<ul className="calian-location-tile__items">
						{displayTileItems.map(({ heading, text, link }, index) => {
							return (
								<li
									className="calian-location-tile__item"
									key={index}
								>
									{(link?.href && link?.href !== '#') ? (
										<a
											className="calian-location-tile__item-wrapper"
											href={link.href}
											target={link.target || '_self'}
											rel={
												link.target
													? 'noopener noreferrer'
													: 'noopener'
											}
										>
											{heading && (
												<h4 className="calian-location-tile__tile-heading">
													{heading}
												</h4>
											)}
											{text && (
												<div
													className="calian-location-tile__tile-text"
													key={text}
													dangerouslySetInnerHTML={{
														__html: text,
													}}
												/>
											)}
										</a>
									) : (
										<div className="calian-location-tile__item-wrapper">
											{heading && (
												<h4 className="calian-location-tile__tile-heading">
													{heading}
												</h4>
											)}
											{text && (
												<div
													className="calian-location-tile__tile-text"
													dangerouslySetInnerHTML={{
														__html: text,
													}}
												/>
											)}
										</div>
									)}
								</li>
							);
						})}
					</ul>
				)}
			</div>
		</div>
	);
});

export default LocationTile;
